/**
 * CORE — the leaderboard scoring formula (Core owns this).
 *
 * `ScoreMetrics.score` is the single sortable integer; HIGHER IS BETTER. The
 * contract only fixes that one integer decides ranking and that "faster finish +
 * more checkpoints => larger score". Everything below is Core's deterministic
 * definition of that.
 *
 * FORMULA (all integer arithmetic on the final result):
 *
 *   For a FINISHED run:
 *     score = FINISH_BONUS
 *           + CHECKPOINT_BONUS * checkpointsReached
 *           + max(0, TIME_BUDGET_MS - finishTimeMs) * TIME_WEIGHT / 1000
 *
 *   For a DNF run (never crossed the finish gate):
 *     score = CHECKPOINT_BONUS * checkpointsReached
 *           + round(distance * DISTANCE_WEIGHT)
 *
 *   The constants are chosen so that:
 *     - ANY finish outranks ANY dnf (FINISH_BONUS dominates the dnf range),
 *     - among finishes, more checkpoints always wins, then faster time,
 *     - among dnfs, more checkpoints wins, then more distance,
 *     - the value is a non-negative 32-bit-safe integer.
 *
 * The formula is a pure function of the metrics, so client and Worker agree.
 *
 * Owned by: Core.
 */

import { TICK_RATE, MAX_RUN_TICKS } from '@/types';
import type { ScoreMetrics, RunStatus } from '@/types';

/** Flat reward for finishing at all — guarantees finishers outrank non-finishers. */
const FINISH_BONUS = 1_000_000;
/** Reward per checkpoint reached (applies to finishes and dnfs alike). */
const CHECKPOINT_BONUS = 50_000;
/** Time budget: a finish at t=0ms earns the full time reward; later finishes earn
 *  proportionally less, down to zero at the budget. Generous so a typical run
 *  lands well inside it. */
const TIME_BUDGET_MS = (MAX_RUN_TICKS / TICK_RATE) * 1000; // 90_000 ms
/** Points per second saved (the (budget - time) term is in ms, /1000 => seconds). */
const TIME_WEIGHT = 5_000;
/** Points per unit of distance for dnf runs (kept below CHECKPOINT_BONUS so a
 *  checkpoint is always worth more than raw distance). */
const DISTANCE_WEIGHT = 100;

export interface ScoringInput {
  readonly status: RunStatus;
  readonly finishTick: number;
  readonly checkpointsReached: number;
  readonly distance: number;
}

/** Compute the canonical, deterministic integer score. */
export function computeScore(input: ScoringInput): number {
  const finished = input.status === 'finished';
  if (finished) {
    const finishTimeMs = (input.finishTick / TICK_RATE) * 1000;
    const timeReward = Math.max(0, Math.round(((TIME_BUDGET_MS - finishTimeMs) * TIME_WEIGHT) / 1000));
    return FINISH_BONUS + CHECKPOINT_BONUS * input.checkpointsReached + timeReward;
  }
  return CHECKPOINT_BONUS * input.checkpointsReached + Math.round(input.distance * DISTANCE_WEIGHT);
}

/** Assemble the full ScoreMetrics from a finished/dnf run. */
export function buildMetrics(input: ScoringInput): ScoreMetrics {
  const finished = input.status === 'finished';
  const finishTick = finished ? input.finishTick : -1;
  const finishTimeMs = finished ? (input.finishTick / TICK_RATE) * 1000 : -1;
  return {
    finishTick,
    finishTimeMs,
    checkpointsReached: input.checkpointsReached,
    distance: input.distance,
    score: computeScore(input),
  };
}
